import { useState, useEffect, useMemo } from 'react';
import {
    Button,
    Container,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Box,
    Paper,
    Avatar,
    TextField,
    InputAdornment
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Refresh } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import services from 'src/services/Services';
import InstanceDataUser from 'src/services/InstanceDataUser';
import WidgetCustomLoader from 'src/widgets/WidgetCustomLoader';
import ExportCSV from 'src/components/ExportCSV';

const Existencias = () => {
    const [dataProducts, setDataProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [legend, setLegend] = useState('');
    const [report, setReport] = useState(2);
    const [searchText, setSearchText] = useState('');

    const filteredRows = useMemo(() => {
        return dataProducts.filter((row) =>
            Object.values(row).some((value) =>
                String(value).toLowerCase().includes(searchText.toLowerCase())
            )
        );
    }, [dataProducts, searchText]);

    const renderBullets = (html) => {
        if (!html) return null;

        const items = html
            .replace(/<\/?ul>/g, '')
            .split('</li>')
            .map(item => item.replace('<li>', '').trim())
            .filter(Boolean);

        return (
            <Box sx={{ maxHeight: 80, overflowY: "auto", pr: 1 }}>
                <ul style={{ paddingLeft: 16, margin: 0 }}>
                    {items.map((item, index) => (
                        <li key={index} style={{ fontSize: 12 }}>
                            {item}
                        </li>
                    ))}
                </ul>
            </Box>
        );
    };

    const columnsFull = [
        { field: 'Articulo', headerName: 'Artículo', width: 100 },
        { field: 'Descripcion', headerName: 'Descripción', width: 400 },
        { field: 'Precio', headerName: 'Precio', width: 90 },
        { field: 'Moneda', headerName: 'Moneda', width: 100 },
        { field: 'TipoCambio', headerName: 'Tipo Cambio', width: 125 },
        { field: 'Disponible', headerName: 'Disponible', width: 130 },
        { field: 'Almacen', headerName: 'Almacén', width: 100 },
        { field: 'Grupo', headerName: 'Grupo', width: 100 },
        { field: 'SeCompraICM', headerName: 'Se Compra ICM', width: 100 },
        { field: 'EstatusICM', headerName: 'Estatus ICM', width: 100 },
        { field: 'UPC', headerName: 'UPC', width: 100 },
        {
            field: 'Caracteristicas',
            headerName: 'Características',
            minWidth: 300,
            flex: 1,
            renderCell: (params) => renderBullets(params.value)
        },
        {
            field: 'Especificaciones',
            headerName: 'Especificaciones',
            minWidth: 300,
            flex: 1,
            renderCell: (params) => renderBullets(params.value)
        },
        { field: 'Dimensiones', headerName: 'Dimensiones', width: 100 },
        { field: 'Peso', headerName: 'Peso', width: 100 },
        { field: 'ProductName', headerName: 'Product Name', width: 200 },
        { field: 'Sku', headerName: 'Sku', width: 100 },
        { field: 'Familia', headerName: 'Familia', width: 100 }
    ];

    const columnsBasic = [
        { field: 'Articulo', headerName: 'Artículo', width: 100 },
        { field: 'Descripcion', headerName: 'Descripción', width: 400 },
        { field: 'Precio', headerName: 'Precio', width: 90, hide: InstanceDataUser.getPrice() },
        { field: 'Moneda', headerName: 'Moneda', width: 100 },
        { field: 'TipoCambio', headerName: 'Tipo Cambio', width: 125 },
        { field: 'Disponible', headerName: 'Disponible', width: 130 },
        { field: 'Almacen', headerName: 'Almacén', width: 100 },
        { field: 'Grupo', headerName: 'Grupo', width: 100 }
    ];

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await services.getAllProducts();
            const products = response.data.Products.map(product => ({
                id: product.UPC,
                ...product
            }));
            setDataProducts(products);
            setLegend('Inventario actualizado ' + response.data.Fecha);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    
                    {/* TITULO */}
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            Existencias
                        </Typography>
                        <Typography variant="body2">
                            {legend}
                        </Typography>
                    </Box>

                    {/* BOTONES */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {!loading && (
                            <>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<Refresh />}
                                        onClick={fetchProducts}
                                    >
                                        Actualizar Inventario
                                    </Button>

                                    <ToggleButtonGroup
                                        size="small"
                                        value={report}
                                        exclusive
                                        onChange={(e, val) => val && setReport(val)}
                                    >
                                        <ToggleButton value={1}>Completo</ToggleButton>
                                        <ToggleButton value={2}>Básico</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                {/* EXPORTAR */}
                                <ExportCSV
                                    csvData={filteredRows}
                                    fileName="existencias"
                                    columns={report === 2 ? columnsBasic : columnsFull}
                                    typeReport={report}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Paper sx={{ borderRadius: 3 }}>
                <TextField
                    size="small"
                    placeholder="Buscar…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />

                <DataGrid
                    rows={filteredRows}
                    columns={report === 2 ? columnsBasic : columnsFull}
                    getRowId={(row) => row.Articulo}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    components={{
                        Toolbar: GridToolbar,
                        LoadingOverlay: WidgetCustomLoader
                    }}
                    autoHeight
                />
            </Paper>
        </Container>
    );
};

export default Existencias;